import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { loadTickers } from '../../services/api';
import './MarketTicker.css';

const SPEED = 60; // px/sec

export default function MarketTicker() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const wrapRef = useRef(null);
  const listRef = useRef(null); // measures one logical list
  const offsetRef = useRef(0);
  const widthRef = useRef(0);
  const rafRef = useRef(0);
  const lastTRef = useRef(0);
  const [copies, setCopies] = useState(3); // number of list copies to render
  const [measured, setMeasured] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const tickers = await loadTickers();
        setItems(tickers || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Measure one list and decide how many copies are needed (>= 2.5x container)
  const recalc = () => {
    const wrap = wrapRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;
    const wrapW = wrap.getBoundingClientRect().width;
    const listW = Math.ceil(list.getBoundingClientRect().width);
    if (listW === 0) return;

    widthRef.current = listW;
    // Enough copies to always cover during the single-frame wrap
    const needed = Math.max(3, Math.ceil((wrapW * 2.5) / listW));
    setCopies(needed);
    setMeasured(true);
    offsetRef.current = 0; // reset position after re-measure
  };

  useLayoutEffect(() => {
    let ro;
    const ready = async () => {
      // wait fonts if supported to avoid reflow mismatch
      try { await document.fonts?.ready; } catch {}
      recalc();
      if (wrapRef.current) {
        ro = new ResizeObserver(() => recalc());
        ro.observe(wrapRef.current);
      }
    };
    ready();
    return () => ro?.disconnect();
    // re-run when items change
  }, [items]);

  // RAF scroller: no DOM reparenting, just offset modulo width
  useEffect(() => {
    if (!measured) return;
    const step = (t) => {
      if (!lastTRef.current) lastTRef.current = t;
      const dt = Math.min(100, t - lastTRef.current);
      lastTRef.current = t;

      const w = widthRef.current || 1;
      offsetRef.current -= (SPEED * dt) / 1000;
      // wrap seamlessly; extra copies hide the single-frame wrap
      if (offsetRef.current <= -w) {
        offsetRef.current += w;
      }

      // apply transform
      const track = wrapRef.current?.querySelector('.ticker__track');
      if (track) track.style.transform = `translateX(${offsetRef.current}px)`;

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [measured]);

  const content = useMemo(() => {
    const data = items ?? [];
    if (!data.length) return 'Loading tickers…';
    return data.map((item, i) => (
      <span key={i} className="market-item">
        <span className="market-symbol">{item.name}</span>
        <span className="market-price">${Number(item.close).toLocaleString()}</span>
      </span>
    ));
  }, [items]);

  return (
    <div className="ticker" ref={wrapRef}>
      <div className="ticker__track">
        {/* First copy is the one we measure */}
        <div className="ticker__list" ref={listRef}>
          {loading || !items.length ? '' : content}
        </div>
        {/* Render extra copies declaratively (no DOM mutations) */}
        {Array.from({ length: Math.max(0, copies - 1) }).map((_, k) => (
          <div className="ticker__list" aria-hidden key={k}>
            {loading || !items.length ? '' : content}
          </div>
        ))}
      </div>
    </div>
  );
}
